/**
 * Courses and lectures of the partner school «Добро и Небо» (https://бпла-добро-небо.рф/). The partner runs
 * them; the booking request comes to «Изнанка» like any excursion lead (its `excursion` column holds the course id).
 * Texts follow the partner's programmes (docs/dobro-i-nebo-courses.json).
 */
export const coursePartner = {
  name: 'Добро и Небо',
  url: 'https://xn-----6kcdcbsk2czaibecv.xn--p1ai/',
  displayUrl: 'бпла-добро-небо.рф',
  logo: '/media/partners/dobro-i-nebo.svg',
} as const;

export const courseValues = [
  'dn_first_flights', 'dn_fpv_repair', 'dn_uas_mission', 'dn_agro_drones', 'dn_ai_cartoons', 'dn_ai_3d',
  'dn_uas_operator', 'dn_uav_mechanic', 'dn_uas_specialist', 'dn_pilot_instructor', 'dn_ground_control', 'dn_uav_data',
] as const;
export type Course = (typeof courseValues)[number];

export const courseAudienceValues = ['kids', 'adults'] as const;
export type CourseAudience = (typeof courseAudienceValues)[number];
export const courseAudienceLabels: Record<CourseAudience, string> = { kids: 'Детям и подросткам', adults: 'Взрослым' };

export type CourseDetails = {
  audience: CourseAudience;
  title: string;
  /** Total volume: academic hours for children, hours for adults. */
  volume: string;
  /** Who the course is for, one sentence. */
  lead: string;
  result: string;
  /** Starting price, «от 18 000 ₽». */
  price: string;
  learn: readonly string[];
  how: string;
  /** Split of the volume: theory, practice, self-study. */
  hours: readonly string[];
  certificate: string;
  /** Programme name in the partner's documents. */
  officialName: string;
};

const kidsCertificate = 'После успешного завершения курса выдаётся сертификат об окончании обучения в школе «Добро и Небо».';
const adultsCertificate = 'После успешного завершения курса выдаётся свидетельство о профессии рабочего или должности служащего.';

export const courses: Record<Course, CourseDetails> = {
  dn_first_flights: {
    audience: 'kids', title: 'Первые полёты на дроне', volume: '24 акад. ч', price: 'от 18 000 ₽',
    lead: 'Для детей, которым хочется разобраться в устройстве дрона и попробовать управлять им.',
    result: 'Освоишь базовые манёвры на симуляторе и управление учебным дроном на защищённой площадке с инструктором.',
    learn: ['Устройство квадрокоптера и органы управления', 'Подготовка техники и проверка по чек-листу', 'Ориентация и базовые манёвры на симуляторе', 'Практика управления на защищённой площадке'],
    how: 'Очные занятия: знакомство с техникой, тренировки на симуляторе и практика с инструктором. В конце — практическое задание и разбор.',
    hours: ['6 ч теории', '18 ч практики'], certificate: kidsCertificate,
    officialName: 'Основы управления беспилотными летательными аппаратами',
  },
  dn_fpv_repair: {
    audience: 'kids', title: 'Устройство и ремонт FPV-дронов', volume: '24 акад. ч', price: 'от 18 000 ₽',
    lead: 'Для детей, которым нравится собирать, разбирать и понимать, как работает техника.',
    result: 'Соберёшь учебный FPV-дрон, научишься находить простые неисправности и выполнять базовое обслуживание.',
    learn: ['Узлы FPV-дрона и их назначение', 'Безопасное обращение с инструментами', 'Поиск простых неисправностей на учебном стенде', 'Разборка, сборка и профилактическое обслуживание'],
    how: 'Очные занятия за рабочим столом и на учебном стенде. Итог — сборка аппарата и карта выполненных работ.',
    hours: ['6 ч теории', '18 ч практики'], certificate: kidsCertificate,
    officialName: 'Основы по ремонту и техническому обслуживанию FPV-дронов',
  },
  dn_uas_mission: {
    audience: 'kids', title: 'Беспилотные системы: от идеи к заданию', volume: '16 акад. ч', price: 'от 18 000 ₽',
    lead: 'Для детей, которым интересно не только пилотирование, но и работа всей команды.',
    result: 'Спланируешь учебную миссию, распределишь роли в команде и разберёшь результаты выполненного задания.',
    learn: ['Состав беспилотной авиационной системы', 'Гражданские задачи применения дронов', 'Подготовка оборудования и распределение ролей', 'Планирование задания и разбор полученных материалов'],
    how: 'Очные занятия с оборудованием и симулятором. Участники проходят путь от подготовки до итогового командного задания.',
    hours: ['5 ч теории', '11 ч практики'], certificate: kidsCertificate,
    officialName: 'Основы по эксплуатации беспилотных авиационных систем',
  },
  dn_agro_drones: {
    audience: 'kids', title: 'Агродроны: технологии для сельского хозяйства', volume: '12 акад. ч', price: 'от 18 000 ₽',
    lead: 'Для детей, которым интересно, как беспилотники помогают сельскому хозяйству.',
    result: 'Составишь маршрут для учебного участка и попробуешь выполнить задание агродрона на симуляторе.',
    learn: ['Гражданское применение агродронов', 'Устройство аппарата на обесточенном оборудовании', 'Учебная карта участка и маршрут', 'Выполнение задания на симуляторе'],
    how: 'Очное знакомство с оборудованием и работа на симуляторе. В завершение — презентация своего учебного задания.',
    hours: ['4 ч теории', '8 ч практики'], certificate: kidsCertificate,
    officialName: 'Основы управления агропромышленными беспилотными летательными аппаратами',
  },
  dn_ai_cartoons: {
    audience: 'kids', title: 'Создание мультфильмов с нейросетями', volume: '16 акад. ч', price: 'от 20 000 ₽',
    lead: 'Для детей, которые любят придумывать истории, персонажей и короткие мультфильмы.',
    result: 'Создашь короткий мультфильм: от идеи и персонажей с помощью ИИ до монтажа и звука.',
    learn: ['Идея истории и раскадровка', 'Создание персонажей и фонов с помощью ИИ', 'Движение и сборка коротких сцен', 'Звук и монтаж ролика'],
    how: 'Очные творческие занятия за компьютером: от замысла до короткого ролика. Итог — показ готовой работы и обсуждение.',
    hours: ['4 ч теории', '12 ч практики'], certificate: kidsCertificate,
    officialName: 'Основы создания анимации с применением генеративных нейросетей',
  },
  dn_ai_3d: {
    audience: 'kids', title: '3D-моделирование с нейросетями', volume: '24 акад. ч', price: 'от 20 000 ₽',
    lead: 'Для детей, которым нравится придумывать предметы и создавать собственные объёмные модели.',
    result: 'Создашь собственную 3D-модель, доработаешь её геометрию и подготовишь проект к показу.',
    learn: ['Простые формы и операции в 3D-среде', 'Эскизы и варианты идеи с помощью ИИ', 'Создание и доработка своей модели', 'Проверка геометрии и подготовка файла к показу'],
    how: 'Очные практические занятия за компьютером. Участник создаёт собственную 3D-модель и представляет итоговый проект.',
    hours: ['5 ч теории', '19 ч практики'], certificate: kidsCertificate,
    officialName: 'Основы 3D-моделирования с применением генеративных нейросетей',
  },
  dn_uas_operator: {
    audience: 'adults', title: 'Оператор беспилотных систем', volume: '75 ч', price: 'от 35 000 ₽',
    lead: 'Для взрослых, которые хотят освоить управление беспилотниками массой до 30 кг и выполнение полётных заданий.',
    result: 'Отработаете подготовку беспилотника к полёту, управление на симуляторе и выполнение учебных полётных заданий.',
    learn: ['Основы навигации, метеорологии и аэродинамики', 'Подготовка и обслуживание оборудования', 'Лётная эксплуатация и обработка информации', 'Полёты на симуляторе и в ручном режиме'],
    how: 'Очные лекции, практические задания и самостоятельная работа. Пилотирование отрабатывается на симуляторе и в ходе практики; предусмотрен итоговый контроль.',
    hours: ['18 ч лекций', '32 ч практики', '25 ч самостоятельной работы'], certificate: adultsCertificate,
    officialName: 'Оператор беспилотных авиационных систем (с максимальной взлётной массой 30 килограммов и менее)',
  },
  dn_uav_mechanic: {
    audience: 'adults', title: 'Механик по ремонту и обслуживанию БВС', volume: '75 ч', price: 'от 35 000 ₽',
    lead: 'Для взрослых, которым интересны электроника, устройство и техническое обслуживание беспилотников до 30 кг.',
    result: 'Освоите базовые операции диагностики, технического обслуживания и ремонта беспилотной системы.',
    learn: ['Электротехника и электроника', 'Основы управления техническими системами', 'Обслуживание оборудования и полезной нагрузки', 'Техническое обслуживание и ремонт беспилотных систем'],
    how: 'Очные лекции и практические занятия по устройству и обслуживанию техники, самостоятельная работа и итоговый контроль.',
    hours: ['18 ч лекций', '32 ч практики', '25 ч самостоятельной работы'], certificate: adultsCertificate,
    officialName: 'Механик по ремонту и техническому обслуживанию беспилотных воздушных судов с максимальной взлётной массой 30 кг и менее',
  },
  dn_uas_specialist: {
    audience: 'adults', title: 'Специалист по эксплуатации БАС', volume: '104 ч', price: 'от 50 000 ₽',
    lead: 'Для взрослых, которые хотят освоить комплексную работу с беспилотной системой: от подготовки до обработки результатов.',
    result: 'Отработаете полный цикл учебной задачи: подготовку системы, пилотирование, обслуживание и разбор полученной информации.',
    learn: ['Подготовка и выполнение полётного задания', 'Лётная эксплуатация и обработка информации', 'Техническое обслуживание и ремонт', 'Практическое управление беспилотным воздушным судном'],
    how: 'Очное обучение сочетает лекции, самостоятельную работу и практику. В программу входит отдельный практикум по пилотированию.',
    hours: ['37 ч лекций', '28 ч практики', '39 ч самостоятельной работы'], certificate: adultsCertificate,
    officialName: 'Специалист по эксплуатации беспилотных авиационных систем, включающих в себя одно или несколько беспилотных воздушных судов с максимальной взлётной массой 30 кг и менее',
  },
  dn_pilot_instructor: {
    audience: 'adults', title: 'Инструктор по обучению пилотов', volume: '105 ч', price: 'от 50 000 ₽',
    lead: 'Для взрослых, которые хотят развиваться в направлении обучения операторов беспилотников до 30 кг.',
    result: 'Освоите основы методики обучения пилотов и отработаете практические навыки управления и обслуживания БВС.',
    learn: ['Устройство БПЛА и особенности эксплуатации', 'Подготовка полёта и анализ информации', 'Методическая подготовка инструкторского состава', 'Практическое управление и обслуживание БВС'],
    how: 'Очные теоретические и практические занятия, самостоятельная работа. Методическая подготовка сочетается с практикумом по пилотированию.',
    hours: ['37 ч лекций', '26 ч практики', '42 ч самостоятельной работы'], certificate: adultsCertificate,
    officialName: 'Инструктор по обучению операторов (внешних пилотов) беспилотных воздушных судов с максимальной взлётной массой 30 кг и менее',
  },
  dn_ground_control: {
    audience: 'adults', title: 'Оператор наземных средств управления', volume: '40 ч', price: 'от 35 000 ₽',
    lead: 'Для взрослых, которым интересны наземная станция, планирование полётов и контроль состояния дрона.',
    result: 'Настроите наземную станцию, подготовите полётное задание и научитесь контролировать состояние борта по телеметрии.',
    learn: ['Настройка наземной станции управления', 'Телеметрия и мониторинг состояния борта', 'Связь и спутниковая навигация', 'Создание, проверка и выполнение полётного задания'],
    how: 'Очные занятия с акцентом на практику управления через наземную станцию и планирование заданий. Предусмотрена самостоятельная работа.',
    hours: ['12 ч лекций', '22 ч практики', '6 ч самостоятельной работы'], certificate: adultsCertificate,
    officialName: 'Оператор наземных средств управления беспилотным летательным аппаратом',
  },
  dn_uav_data: {
    audience: 'adults', title: 'Получение и обработка данных с БВС', volume: '40 ч', price: 'от 35 000 ₽',
    lead: 'Для взрослых, которым интересны картография, мониторинг, агроанализ и работа с результатами съёмки.',
    result: 'Спланируете учебную съёмку, проверите и организуете полученные данные, подготовите отчёт с визуализацией результатов.',
    learn: ['Параметры съёмки и планирование миссии', 'Подготовка БПЛА и полезной нагрузки', 'Приём, проверка, организация и экспорт данных', 'Отчёты и визуализация результатов'],
    how: 'Очные занятия: создание и симуляция миссии, практические задания по работе с данными и отчётами, самостоятельная работа.',
    hours: ['8 ч лекций', '26 ч практики', '6 ч самостоятельной работы'], certificate: adultsCertificate,
    officialName: 'Специалист по получению и обработке данных с беспилотных воздушных судов',
  },
};

/** Courses of one audience in catalog order. */
export const coursesFor = (audience: CourseAudience): Course[] => courseValues.filter((course) => courses[course].audience === audience);
